import { createContext, useContext, useMemo, useRef, useCallback } from 'react';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';

type CustomWorklistLoadingContextBag = {
  isProcessingFiltersRef: React.MutableRefObject<boolean>;
  requestedIdsRef: React.MutableRefObject<string | null>;
  currentRequestRef: React.MutableRefObject<string | null>;
  setLoadingRefs: (requestKey: string) => void;
  clearLoadingRefs: (requestKey: string) => void;
  updateLoadingRefs: (
    requestKey: string,
    legacyRef?: React.MutableRefObject<string | null>
  ) => void;
};

const CustomWorklistLoadingContext = createContext<CustomWorklistLoadingContextBag | null>(null);

export function CustomWorklistLoadingContextProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const isProcessingFiltersRef = useRef<boolean>(false);
  const requestedIdsRef = useRef<string | null>(null);
  const currentRequestRef = useRef<string | null>(null);
  const [isRefactorEnabled] = useFeatureFlagEnabled(FF.WORKLIST_CONTEXT_REFACTOR);

  const setLoadingRefs = useCallback(
    (requestKey: string) => {
      // If refactor is disabled, skip setting the context refs
      if (isRefactorEnabled === false) return;

      requestedIdsRef.current = requestKey;
      currentRequestRef.current = requestKey;
      isProcessingFiltersRef.current = true;
    },
    [isRefactorEnabled]
  );

  const clearLoadingRefs = useCallback(
    (requestKey: string) => {
      // If refactor is disabled, skip clearing the context refs
      if (isRefactorEnabled === false) return;

      let clearedCurrentRequest = false;

      if (currentRequestRef.current === requestKey) {
        currentRequestRef.current = null;
        clearedCurrentRequest = true;
      }

      if (requestedIdsRef.current === requestKey) {
        requestedIdsRef.current = null;
      }

      if (clearedCurrentRequest) {
        isProcessingFiltersRef.current = false;
      }
    },
    [isRefactorEnabled]
  );

  const updateLoadingRefs = useCallback(
    (requestKey: string, legacyRef?: React.MutableRefObject<string | null>) => {
      // If refactor is disabled, update the legacy context refs
      const targetRequestedRef =
        isRefactorEnabled === false && legacyRef ? legacyRef : requestedIdsRef;

      if (targetRequestedRef) {
        targetRequestedRef.current = requestKey;
      }

      // Only set processing refs if refactor is enabled
      if (isRefactorEnabled !== false) {
        currentRequestRef.current = requestKey;
        isProcessingFiltersRef.current = true;
      }
    },
    [isRefactorEnabled]
  );

  const value = useMemo(() => {
    return {
      isProcessingFiltersRef,
      requestedIdsRef,
      currentRequestRef,
      setLoadingRefs,
      clearLoadingRefs,
      updateLoadingRefs,
    } as CustomWorklistLoadingContextBag;
  }, [setLoadingRefs, clearLoadingRefs, updateLoadingRefs]);

  return (
    <CustomWorklistLoadingContext.Provider value={value}>
      {children}
    </CustomWorklistLoadingContext.Provider>
  );
}

export function useCustomWorklistLoadingContext(): CustomWorklistLoadingContextBag {
  const ctx = useContext(CustomWorklistLoadingContext);
  if (!ctx) {
    throw new Error(
      'useCustomWorklistLoadingContext must be used within a CustomWorklistLoadingContextProvider.'
    );
  }
  return ctx;
}
